// FILE: src/services/appVersion.js
// "A new version is available": deploys write the frontend commit to
// /REVISION next to the web build (ops/deploy.sh web → build/REVISION). The
// app remembers the revision it found when it started, re-reads /REVISION
// every 5 minutes and when the tab is shown again, and reports when it
// differs. It never reloads by itself (a student may be mid-lesson).
// No /REVISION (dev and test servers answer 404 or the index.html fallback)
// or a failed request → nothing happens.

import { webFetch } from "./api";

export const REVISION_URL = "/REVISION";
export const CHECK_EVERY_MS = 5 * 60_000;

// A commit id or similar short token; anything else (HTML, JSON) is not a revision.
const REVISION = /^[0-9A-Za-z][0-9A-Za-z._-]{0,63}$/;

/** The deployed revision, or null when there is none / it can't be read. */
export async function fetchRevision() {
  try {
    const res = await webFetch(REVISION_URL, { cache: "no-store" });
    if (!res || !res.ok || typeof res.text !== "function") return null;
    const text = String(await res.text()).trim();
    return REVISION.test(text) ? text : null;
  } catch {
    return null;
  }
}

let state = { current: null, latest: null, updateAvailable: false };
const listeners = new Set();
let users = 0;
let timer = null;
let checking = null;
let generation = 0; // bumped by resetVersionWatch: older stop() calls do nothing

function update(next) {
  state = { ...state, ...next };
  listeners.forEach((l) => l());
}

/** Read /REVISION once: the first answer is this app's own revision. */
export function checkNow() {
  if (checking) return checking;
  checking = (async () => {
    const rev = await fetchRevision();
    if (!rev) return;
    if (!state.current) update({ current: rev });
    else if (rev !== state.current && rev !== state.latest) update({ latest: rev, updateAvailable: true });
  })().finally(() => {
    checking = null;
  });
  return checking;
}

const onFocus = () => checkNow();
const onVisible = () => {
  if (document.visibilityState === "visible") checkNow();
};

/** Start watching (shared by every mounted banner); returns stop(). */
export function watchVersion() {
  users += 1;
  if (users === 1) {
    if (!state.current) checkNow();
    timer = setInterval(checkNow, CHECK_EVERY_MS);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
  }
  const mine = generation;
  let stopped = false;
  return () => {
    if (stopped || mine !== generation) return;
    stopped = true;
    users -= 1;
    if (users === 0) {
      clearInterval(timer);
      timer = null;
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    }
  };
}

export function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export const getVersionState = () => state;

/** Tests only: forget the remembered revision and stop watching. */
export function resetVersionWatch() {
  generation += 1;
  if (timer) clearInterval(timer);
  timer = null;
  users = 0;
  checking = null;
  window.removeEventListener("focus", onFocus);
  document.removeEventListener("visibilitychange", onVisible);
  state = { current: null, latest: null, updateAvailable: false };
  listeners.clear();
}
